// Aangeleverde bestanden bij een crematiedossier: uploaden en weer ophalen. Twee aanroepen die de
// gewone dataProvider niet kan maken — een multipart-body en een binaire download met een bearer —
// dus dezelfde vorm als src/api/relatieMedia.ts en src/api/intakePhoto.ts.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export class CrematieDocumentError extends Error {
  constructor(
    readonly status: number,
    readonly serverMelding?: string,
  ) {
    super(serverMelding || `HTTP ${status}`);
    this.name = "CrematieDocumentError";
  }
}

/** Wat de api teruggeeft over een net aangeleverd document. */
export interface AangeleverdDocument {
  id: string;
  bestandsnaam: string | null;
  grootte: number | null;
}

/**
 * Een bestand aanleveren bij een dossier. Geen Content-Type meegeven: die zet de browser zelf,
 * inclusief de multipart-grens.
 */
export async function uploadCrematieDocument(
  dossierId: string,
  file: File,
  omschrijving?: string,
): Promise<AangeleverdDocument> {
  const form = new FormData();
  form.append("file", file);
  if (omschrijving) form.append("omschrijving", omschrijving);
  const res = await fetch(`${API_URL}/crematorium/dossiers/${dossierId}/documenten/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) {
    // De api geeft bij een 400 een {message} mee — te groot, leeg, onbekend dossier. Dat is de
    // enige tekst die de gebruiker echt verder helpt, dus die halen we eruit als hij er is.
    let melding: string | undefined;
    try {
      melding = ((await res.json()) as { message?: string }).message;
    } catch {
      melding = undefined;
    }
    throw new CrematieDocumentError(res.status, melding);
  }
  return (await res.json()) as AangeleverdDocument;
}

/** Het bestand ophalen en door de browser laten opslaan. */
export async function downloadCrematieDocument(
  dossierId: string,
  documentId: string,
  bestandsnaam: string,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/crematorium/dossiers/${dossierId}/documenten/${documentId}/bestand`,
    { headers: authHeaders() },
  );
  if (!res.ok) throw new CrematieDocumentError(res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = bestandsnaam || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
